<template>
    <authenticated-layout>
        <v-card class="pa-6">
            <v-card-title
                class="d-flex justify-space-between align-center pa-0 py-3"
            >
                <v-btn
                    variant="flat"
                    size="small"
                    color="primary"
                    @click="openModal()"
                >
                    {{ $t("inverter.addNewOne") }}
                </v-btn>

                <div class="d-flex justify-space-between align-center">
                    <v-select
                        v-model="inverterSelectedBrand"
                        :items="[
                            { title: 'All', value: 'all' },
                            ...brands.map((o) => ({ title: o, value: o })),
                        ]"
                        :label="$t('inverter.Filter by brand')"
                        variant="outlined"
                        density="compact"
                        style="width: 230px"
                        @update:modelValue="changeSelectedBrand()"
                    />
                </div>
            </v-card-title>

            <div class="overflow-x-auto">
                <table>
                    <tr>
                        <th
                            v-for="(header, index) in headers"
                            :key="`table-header-${index}`"
                        >
                            {{ header }}
                        </th>
                    </tr>

                    <tr v-for="row in data" :key="`heatpump-${row.id}`">
                        <td>{{ row.id }}</td>
                        <td>{{ row.brand }}</td>
                        <td>{{ row.partner?.name ?? "-" }}</td>
                        <td>{{ row.model }}</td>
                        <td>{{ row.price }}</td>
                        <td>{{ row.phases }}</td>
                        <td>{{ row.warranty }}</td>
                        <td>{{ row.warranty_extension }}</td>
                        <td>{{ row.kw }}</td>
                        <td>{{ row.max_kwp }}</td>
                        <td>{{ row.sales_points }}</td>
                        <td>
                            <img :src="row.image_url" width="50" />
                        </td>
                        <td class="truncate max-w-xs">
                            {{ row.selling_points }}
                        </td>
                        <td>{{ row.series ?? '-' }}</td>
                        <td>{{ row.tagline ?? '-' }}</td>
                        <td>{{ row.compatible_battery_brands }}</td>
                        <td>
                            <div class="d-flex flex-column">
                                <ul v-if="row.datasheets.length">
                                    <li
                                        v-for="datasheet in row.datasheets"
                                        :key="`datasheet-${datasheet.id}`"
                                    >
                                        <a
                                            :href="`/datasheet/${datasheet.id}`"
                                            :title="datasheet.name"
                                        >
                                            ⬇️
                                        </a>
                                    </li>
                                </ul>

                                <div class="mt-1">
                                    <v-btn
                                        v-if="row.datasheets.length"
                                        variant="flat"
                                        size="x-small"
                                        color="error"
                                        @click="
                                            onRemoveUploadDatasheets(row.id)
                                        "
                                    >
                                        {{ $t("inverter.Remove") }}
                                    </v-btn>

                                    <v-btn
                                        v-else
                                        variant="flat"
                                        size="x-small"
                                        color="primary"
                                        @click="
                                            onOpenUploadDatasheetsModal(row.id)
                                        "
                                    >
                                        {{ $t("inverter.Add") }}
                                    </v-btn>
                                </div>
                            </div>
                        </td>
                        <td>
                            <input
                                type="checkbox"
                                @change="updateVisibility(row)"
                                :checked="row.visible"
                                class="bg-gray-200"
                            />
                        </td>
                        <td>
                            <input
                                type="checkbox"
                                @change="updateHybridCapability(row)"
                                :checked="row.is_hybrid"
                                class="bg-gray-200"
                            />
                        </td>
                        <td class="text-right">
                            <div class="d-flex flex-col">
                                <v-btn
                                    variant="flat"
                                    size="x-small"
                                    color="primary"
                                    @click="edit(row)"
                                >
                                    {{ $t("inverter.edit") }}
                                </v-btn>

                                <v-btn
                                    variant="flat"
                                    size="x-small"
                                    class="mt-1"
                                    color="error"
                                    @click="deleteRow(row)"
                                >
                                    {{ $t("inverter.delete") }}
                                </v-btn>
                            </div>
                        </td>
                    </tr>
                </table>
            </div>

            <v-dialog v-model="isOpen" max-width="700" persistent scrollable>
                <v-form
                    ref="form"
                    @submit.prevent="onSubmit">
                    <v-card>
                        <v-card-title class="text-subtitle-1">
                            {{ editMode ? $t("inverter.edit") : $t("inverter.addNewOne") }}
                        </v-card-title>

                        <v-divider />

                        <v-card-text style="max-height: 600px">
                            <v-text-field
                                v-model="form.brand"
                                :label="$t('inverter.brand')"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :rules="[$validationRules.required]"
                                :error-messages="
                                    errors.brand ? [errors.brand[0]] : []
                                "
                            />

                            <v-text-field
                                v-model="form.model"
                                :label="$t('inverter.model')"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :rules="[$validationRules.required]"
                                :error-messages="
                                    errors.model ? [errors.model[0]] : []
                                "
                            />

                            <v-text-field
                                v-model="form.price"
                                :label="$t('inverter.price')"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :rules="[$validationRules.required, $validationRules.number]"
                                :error-messages="
                                    errors.price ? [errors.price[0]] : []
                                "
                            />

                            <v-text-field
                                v-model="form.phases"
                                :label="$t('inverter.phases')"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :rules="[$validationRules.required, $validationRules.number]"
                                :error-messages="
                                    errors.phases ? [errors.phases[0]] : []
                                "
                            />

                            <v-text-field
                                v-model="form.warranty"
                                :label="$t('inverter.warranty')"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :rules="[$validationRules.required, $validationRules.number]"
                                :error-messages="
                                    errors.warranty ? [errors.warranty[0]] : []
                                "
                            />

                            <v-text-field
                                v-model="form.warranty_extension"
                                :label="$t('inverter.warrantyExtension')"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :rules="[$validationRules.required, $validationRules.number]"
                                :error-messages="
                                    errors.warranty_extension
                                        ? [errors.warranty_extension[0]]
                                        : []
                                "
                            />

                            <v-text-field
                                v-model="form.kw"
                                label="KW"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :rules="[$validationRules.required, $validationRules.number]"
                                :error-messages="errors.kw ? [errors.kw[0]] : []"
                            />

                            <v-text-field
                                v-model="form.max_kwp"
                                :label="$t('inverter.maxKwp')"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :rules="[$validationRules.required, $validationRules.number]"
                                :error-messages="
                                    errors.max_kwp ? [errors.max_kwp[0]] : []
                                "
                            />

                            <v-text-field
                                v-model="form.selling_points"
                                :label="$t('inverter.sellingPoints')"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :rules="[$validationRules.required]"
                                :error-messages="
                                    errors.selling_points
                                        ? [errors.selling_points[0]]
                                        : []
                                "
                            />

                            <v-text-field
                                v-model="form.compatible_battery_brands"
                                :label="$t('inverter.compatibleBatteries')"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :error-messages="
                                    errors.compatible_battery_brands
                                        ? [errors.compatible_battery_brands[0]]
                                        : []
                                "
                            />

                            <v-select
                                v-model="form.partner_id"
                                :label="$t('inverter.Partner')"
                                :items="partnersList"
                                variant="outlined"
                                density="compact"
                                type="input"
                                :error-messages="
                                    errors.partner_id ? [errors.partner_id[0]] : []
                                "
                            />

                            <v-text-field
                                v-model="form.sales_points"
                                :label="$t('inverter.Sales points')"
                                variant="outlined"
                                density="compact"
                                type="number"
                                :rules="[$validationRules.required, $validationRules.number, $validationRules.min(form.sales_points, 0.5)]"
                                :error-messages="
                                    errors.sales_points
                                        ? [errors.sales_points[0]]
                                        : []
                                "
                            />

                            <v-text-field
                                v-model="form.series"
                                :label="$t('inverter.series')"
                                variant="outlined"
                                density="compact"
                                :rules="[]"
                                :error-messages="
                                    errors.series
                                        ? [errors.series[0]]
                                        : []
                                "
                            />

                            <v-text-field
                                v-model="form.tagline"
                                :label="$t('inverter.Tagline')"
                                variant="outlined"
                                density="compact"
                                :rules="[]"
                                :error-messages="
                                    errors.tagline
                                        ? [errors.tagline[0]]
                                        : []
                                "
                            />

                            <div class="mb-4">
                                <label
                                    for="product_image"
                                    class="block text-gray-700 text-sm font-bold mb-2"
                                >
                                    {{ $t("heatpump.photo") }}:
                                </label>

                                <div v-if="editMode">
                                    <div
                                        v-show="!uploadNewProductImage"
                                        class="flex justify-start items-center"
                                    >
                                        <img :src="form.image_url" width="70" />
                                        <div>
                                            <button
                                                @click="
                                                    uploadNewProductImage = true
                                                "
                                                type="button"
                                                class="bg-blue-500 hover:bg-blue-700 text-white font-bold ml-3 py-1 px-2 text-sm rounded"
                                            >
                                                {{ $t("Upload new") }}
                                            </button>
                                        </div>
                                    </div>

                                    <v-file-input
                                        v-show="uploadNewProductImage"
                                        id="product_image"
                                        ref="productImage"
                                        accept="image/*"
                                        :rules="uploadNewProductImage ? [$validationRules.required, $validationRules.mimes] : []"
                                    />
                                </div>

                                <div v-else>
                                    <v-file-input
                                        id="product_image"
                                        ref="productImage"
                                        accept="image/*"
                                        :rules="[$validationRules.required, $validationRules.mimes]"
                                    />
                                </div>

                                <div
                                    v-if="errors.product_image"
                                    class="text-red-500"
                                >
                                    {{ errors.product_image[0] }}
                                </div>
                            </div>
                        </v-card-text>

                        <v-divider />

                        <v-card-actions>
                            <v-spacer />

                            <v-btn
                                variant="flat"
                                size="small"
                                @click="closeModal()"
                            >
                                {{ $t("inverter.cancel") }}
                            </v-btn>

                            <v-btn
                                v-show="!editMode"
                                color="secondary"
                                variant="flat"
                                size="small"
                                type="submit"
                            >
                                {{ $t("inverter.save") }}
                            </v-btn>

                            <v-btn
                                v-show="editMode"
                                color="secondary"
                                variant="flat"
                                size="small"
                                type="submit"
                            >
                                {{ $t("inverter.update") }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-form>
            </v-dialog>

            <v-dialog
                v-model="uploadDatasheetsModal"
                max-width="400"
                persistent
            >
                <v-card>
                    <v-card-title class="text-subtitle-1">
                        {{ $t("inverter.Datasheets") }}
                    </v-card-title>

                    <v-divider />

                    <v-card-text>
                        <input
                            type="file"
                            id="datasheets"
                            class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                            ref="datasheets"
                            accept="application/pdf,application/vnd.ms-excel"
                            multiple
                        />
                    </v-card-text>

                    <v-divider />

                    <v-card-actions>
                        <v-spacer />

                        <v-btn
                            variant="flat"
                            size="small"
                            @click="onCloseUploadDatasheetsModal()"
                        >
                            {{ $t("inverter.cancel") }}
                        </v-btn>

                        <v-btn
                            color="secondary"
                            variant="flat"
                            size="small"
                            @click="onUploadDatasheets()"
                        >
                            {{ $t("inverter.Upload") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card>
    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from "@/Layouts/Authenticated";

export default {
    components: {
        AuthenticatedLayout,
    },
    props: ["data", "errors", "brands", "selectedBrand", "partners"],
    data() {
        return {
            headers: [
                "ID",
                this.$t("inverter.brand"),
                this.$t("inverter.Partner"),
                this.$t("inverter.model"),
                this.$t("inverter.price"),
                this.$t("inverter.phases"),
                this.$t("inverter.warranty"),
                this.$t("inverter.warrantyExtension"),
                "KW",
                this.$t("inverter.maxKwp"),
                this.$t('inverter.Sales points'),
                this.$t("inverter.imageUrl"),
                this.$t("inverter.sellingPoints"),
                this.$t("inverter.series"),
                this.$t("inverter.Tagline"),
                this.$t("inverter.compatibleBatteries"),
                this.$t("inverter.Datasheets"),
                this.$t("inverter.visible"),
                this.$t("inverter.hybrid"),
                "",
            ],
            inverterSelectedBrand: this.selectedBrand,
            editMode: false,
            isOpen: false,
            form: {
                id: null,
                brand: null,
                partner_id: null,
                model: null,
                price: null,
                phases: null,
                warranty: null,
                warranty_extension: null,
                kw: null,
                max_kwp: null,
                image_url: null,
                selling_points: null,
                compatible_battery_brands: null,
                sales_points: null,
                series: null,
                tagline: null,
            },
            uploadNewProductImage: false,
            uploadDatasheetsModal: false,
            uploadDatasheetsSelectedId: null,
        };
    },
    computed: {
        partnersList() {
            let list = this.partners.map((partner) => {
                return {
                    title: partner.name,
                    value: partner.id,
                };
            });

            return [
                {
                    title: this.$t("inverter.Default No partner"),
                    value: null,
                },
                ...list,
            ];
        },
    },
    methods: {
        changeSelectedBrand() {
            this.$inertia.get("/inverter", {
                brand: this.inverterSelectedBrand,
            });
        },
        openModal() {
            this.isOpen = true;
        },
        closeModal() {
            this.isOpen = false;
            this.reset();
            this.editMode = false;
            this.uploadNewProductImage = false;
        },
        reset() {
            this.form = {
                id: null,
                brand: null,
                partner_id: null,
                model: null,
                price: null,
                phases: null,
                warranty: null,
                warranty_extension: null,
                kw: null,
                max_kwp: null,
                image_url: null,
                selling_points: null,
                compatible_battery_brands: null,
                sales_points: null,
                series: null,
                tagline: null,
            };
        },
        getDatasheetNames(input) {
            return input.length ? input.map((o) => o.name).join() : "";
        },
        async onSubmit() {
            const { valid } = await this.$refs.form.validate();

            if (valid) {
                this.editMode ? this.update(this.form) : this.save(this.form);
            }
        },
        save(data) {
            data.product_image = this.$refs.productImage.files[0] || null;

            this.$inertia.post("/inverter", data);

            this.reset();
            this.closeModal();

            this.editMode = false;
        },
        edit(data) {
            this.form = Object.assign({}, data);
            this.editMode = true;
            this.openModal();
        },
        update(data) {
            data._method = "PATCH";

            data.product_image = this.$refs.productImage.files[0] || null;

            this.$inertia.post("/inverter/edit/" + data.id, data);

            this.reset();
            this.closeModal();
        },
        deleteRow(data) {
            if (
                !confirm(
                    this.$t("inverter.areYouSureYouWantToDeleteThisInverter")
                )
            )
                return;
            data._method = "DELETE";
            this.$inertia.post("/inverter/delete/" + data.id, data);
            this.reset();
            this.closeModal();
        },
        updateVisibility(data) {
            this.$inertia.post("/inverter/update-visibility", {
                id: data.id,
                flag: !data.visible,
            });
        },
        updateHybridCapability(data) {
            this.$inertia.post("/inverter/update-hybrid-capability", {
                id: data.id,
                flag: !data.is_hybrid,
            });
        },
        onRemoveUploadDatasheets(itemId) {
            this.$inertia.post("/inverter/remove-datasheets", {
                id: itemId,
            });
        },
        onCloseUploadDatasheetsModal() {
            this.uploadDatasheetsSelectedId = null;
            this.uploadDatasheetsModal = false;
        },
        onOpenUploadDatasheetsModal(itemId) {
            this.uploadDatasheetsSelectedId = itemId;
            this.uploadDatasheetsModal = true;
        },
        onUploadDatasheets() {
            this.$inertia.post("/inverter/upload-datasheets", {
                id: this.uploadDatasheetsSelectedId,
                datasheets: this.$refs.datasheets.files,
            });

            this.onCloseUploadDatasheetsModal();
        },
    },
};
</script>

<style scoped>
table {
    width: 100%;
    table-layout: fixed;
    font-size: 12px;
}

@media screen and (max-width: 1024px) {
    table {
        width: auto;
    }
}

th {
    padding: 10px 0px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    box-sizing: border-box;
    padding-left: 10px;
    border-bottom: 1px solid #aaa;
}

td {
    text-align: left;
    box-sizing: border-box;
    padding: 5px 0px;
    padding-left: 10px;
    border-bottom: 1px solid #ccc;
}
</style>
